import { Fragment, useEffect } from 'react'

import { isEmpty } from 'lodash'
import { FormattedMessage } from 'react-intl'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import Flex from 'styled-flex-component'

import AnalyticsAudienceMatchLabel from '@components/Analytics/AudienceMatch/Label/Label'
import AnalyticsIdentityAudienceInsights from '@components/Analytics/Identity/AudienceInsights'
import { Hr } from '@components/UI/Hr/Hr.styled'
import Loading from '@components/UI/Loading'
import { AudienceInsightsSource, HashLinks } from '@enums'
import { useDrawer } from '@hooks/useDrawer'
import { IApplicationState } from '@store'
import {
  fetchAudienceInsights,
  selectAudienceInsights,
  selectAudienceInsightsIsFetching,
} from '@store/audienceInsights'
import {
  selectAudienceInsightsLink,
  selectIdentityId,
  selectIsAudienceMatch,
  selectIsSubmissionFromTiktok,
  selectIsSubmissionFromYoutube,
  selectIsYouTubeBrandConnect,
} from '@store/submissions'
import Theme from '@theme'
import { Container, H3, Icon, Text } from '@tribegroup'
import AnalyticsAudienceMetricsDrawer from './Drawer'

interface IProps {
  campaignId: number
  submissionId: number
}

const AnalyticsAudienceMetricsWrapper: React.FC<IProps> = ({ campaignId, submissionId }) => {
  const dispatch = useDispatch()
  const {
    identityId,
    hasAudienceInsightsLink,
    isAudienceMatch,
    isFetching,
    isTikTok,
    isYouTubeBrandConnect,
    isYoutube,
  } = useSelector((state: IApplicationState) => {
    const identityId = selectIdentityId(state, submissionId)
    const audienceInsightsLink = selectAudienceInsightsLink(state, submissionId)

    return {
      hasAudienceInsightsLink: Boolean(audienceInsightsLink),
      hasAudienceInsights: !isEmpty(selectAudienceInsights(state, identityId)),
      isFetching: selectAudienceInsightsIsFetching(state),
      isAudienceMatch: selectIsAudienceMatch(state, submissionId),
      isTikTok: selectIsSubmissionFromTiktok(state, submissionId),
      isYoutube: selectIsSubmissionFromYoutube(state, submissionId),
      isYouTubeBrandConnect: selectIsYouTubeBrandConnect(state, submissionId),
      identityId,
    }
  }, shallowEqual)

  const isYtBrandConnectFiltered = isYouTubeBrandConnect

  useEffect(() => {
    if (identityId) {
      dispatch(fetchAudienceInsights(identityId, AudienceInsightsSource.Submission, submissionId))
    }
  }, [identityId, submissionId, dispatch])

  if (isFetching) {
    return <Loading />
  }

  if (!hasAudienceInsightsLink) {
    return <Fragment />
  }

  const { showDrawer } = useDrawer()

  const learnMoreOnClick = () => {
    showDrawer(
      <AnalyticsAudienceMetricsDrawer campaignId={campaignId} />,
      { showHeaderDivider: true },
      <FormattedMessage id="core.text.audienceMetrics" />,
    )
  }

  const isYouTubeBrandConnectSubmission = isYoutube && isYouTubeBrandConnect
  const isLearnMoreLinkVisible = !isYouTubeBrandConnectSubmission
  const isAudienceMatchLabelVisible = isAudienceMatch && !isYouTubeBrandConnectSubmission

  return (
    <Fragment>
      {!isYtBrandConnectFiltered && <Hr backgroundColor={Theme.grey400Color} />}
      <Container topOuterSpacing={2.5} bottomOuterSpacing={4} id={HashLinks.AudienceInsights}>
        <Container bottomOuterSpacing={1.625}>
          <Flex full justifyBetween alignCenter>
            <Container>
              <H3 inline color={Theme.grey900Color}>
                <FormattedMessage id="core.text.audienceMetrics" />
              </H3>
              {isLearnMoreLinkVisible && (
                <Container inlineBlock pointer verticalAlign="middle" leftOuterSpacing={1.5}>
                  <Text
                    customFontSize={0.8125}
                    lineHeight={1}
                    color={Theme.grey900Color}
                    uppercase
                    customletterSpacing="1.5px"
                    onClick={learnMoreOnClick}
                    data-testid="learn-more-button"
                  >
                    <FormattedMessage id="core.text.learnMore" />
                  </Text>
                  <Icon
                    size={1.5}
                    glyph="chevron-right"
                    color={Theme.grey900Color}
                    verticalAlign="middle"
                  />
                </Container>
              )}
            </Container>

            {isAudienceMatchLabelVisible && (
              <AnalyticsAudienceMatchLabel submissionId={submissionId} campaignId={campaignId} />
            )}
          </Flex>
        </Container>
        {hasAudienceInsightsLink && (
          <AnalyticsIdentityAudienceInsights
            identityId={identityId}
            isTikTok={isTikTok}
            submissionId={submissionId}
          />
        )}
      </Container>
    </Fragment>
  )
}

export default AnalyticsAudienceMetricsWrapper
